import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, Save, X, AlertCircle,
  CheckCircle, FileText, Upload, Star, Eye,
  Calendar, Clock
} from 'lucide-react';

const BlogManagement = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    readTime: '',
    image: '',
    featured: false,
    date: new Date().toISOString().split('T')[0]
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    'AI Strategy', 'Digital Transformation', 'Business Strategy',
    'AI Implementation', 'Startup Growth', 'Security',
    'Process Design', 'Technology', 'Innovation'
  ];

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      const res = await fetch('http://localhost:5000/api/blog');
      if (!res.ok) throw new Error('Failed to load posts');
      const data = await res.json();
      const sortedData = data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setBlogPosts(sortedData);
    } catch (err) {
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const post = blogPosts.find(p => p._id === id);
    if (!post) return;
    setEditingPost(post._id);
    setShowAddForm(true);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content || '',
      author: post.author,
      category: post.category,
      readTime: post.readTime || '',
      image: post.image || '',
      featured: post.featured || false,
      date: post.date.split('T')[0]
    });
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingPost(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      category: '',
      readTime: '',
      image: '',
      featured: false,
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!formData.title.trim() || !formData.excerpt.trim() || !formData.author.trim()) {
      setError('Title, excerpt, and author are required');
      return;
    }
    try {
      const url = editingPost
        ? `http://localhost:5000/api/blog/${editingPost}`
        : 'http://localhost:5000/api/blog';
      const method = editingPost ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || (editingPost ? 'Failed to update blog post' : 'Failed to add blog post'));
      }
      setSuccess(editingPost ? 'Blog post updated successfully' : 'Blog post added successfully');
      await loadBlogPosts();
      handleCancel();
    } catch (err) {
      setError(err.message || 'Failed to save blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      const res = await fetch(`http://localhost:5000/api/blog/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete blog post');
      setSuccess('Blog post deleted successfully');
      await loadBlogPosts();
    } catch (err) {
      setError(err.message || 'Failed to delete blog post');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingPost(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const filteredPosts = filter === 'all'
    ? blogPosts
    : filter === 'featured'
    ? blogPosts.filter(post => post.featured)
    : blogPosts.filter(post => post.category === filter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Blog Management</h1>
          <p className="text-gray-600 mt-1 md:mt-2">Manage your blog posts and articles</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 w-full md:w-auto"
        >
          <Plus className="w-4 h-4 md:w-5 md:h-5" />
          <span>Add Blog Post</span>
        </motion.button>
      </div>
      {/* ...rest of the component remains unchanged (form, alerts, posts list, etc.) */}
    </div>
  );
};

export default BlogManagement;
